<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import TableFilters from '@/components/common/TableFilters.vue'
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'
import { usePremiumInvoices } from '@/composables/asicw/premium-invoices'
import { NotifyFail } from '@/helpers/core/notifications'
import { currencyFormatter, dateFormatter } from '@/helpers/ep/formatters'
import type { IPremiumInvoice, IPremiumInvoiceFilters } from '@/types/asicw/invoices'
import { InvoiceStatus, InvoiceStatusOptions, InvoiceType } from '@/types/asicw/invoices'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { downloadLinkTarget, getCostColor, getCssVarValue, hex2rgba } from '@/util'
import { formatCurrency } from '@/util/formatters.ts'
import { Icon } from '@iconify/vue'
import { watchDebounced } from '@vueuse/core'
import { vMaska } from 'maska/vue'
import { reactive, ref } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()

// Initialize filters with route query parameters to avoid double query execution
const filters = reactive<IPremiumInvoiceFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  id: route.query.id ? parseInt(route.query.id as string) : undefined,
  start_date: undefined,
  end_date: undefined,
  status: undefined,
  customer_id: undefined,
  policy_group_number: undefined,
  type: undefined,
})

// Filter fields
const invoiceIDInput = ref<string>(route.query.id?.toString() ?? '')
const policyGroupInput = ref<string>('')
const statusFilter = ref<string>()
const typeFilter = ref<string>()
const dateRangeFilter = ref<[Date, Date] | undefined>()

// Status editing state
const editingStatus = ref<Record<number, boolean>>({})
const selectedStatus = ref<Record<number, InvoiceStatus | undefined>>({})

// Status color mapping similar to priority color mapping
const statusColorMap: Record<string, string> = {
  [InvoiceStatus.READY_TO_SEND]: 'var(--claim-invoice-status-ready-to-send)',
  [InvoiceStatus.SENT]: 'var(--claim-invoice-status-sent)',
  [InvoiceStatus.PAYMENT_LATE]: 'var(--claim-invoice-status-payment-late)',
  [InvoiceStatus.PAID]: 'var(--claim-invoice-status-paid)',
}

// Helper functions for status display
const getStatusColor = (status: string): string => {
  return statusColorMap[status] || 'var(--status-default)'
}

const getStatusTagColor = (status: string): string | undefined => {
  const colorVar = getStatusColor(status)
  const colorValue = getCssVarValue(colorVar)
  if (!colorValue) return undefined
  try {
    return hex2rgba(colorValue, 0.2)
  } catch {
    return undefined
  }
}

// Vue Query
const { getPremiumInvoicesQuery, deletePremiumInvoiceMutation, updatePremiumInvoiceMutation } = usePremiumInvoices()
const { isFetching, isError, isRefetching, data, error, refetch } = getPremiumInvoicesQuery(filters)

watchDebounced(
  invoiceIDInput,
  (value) => {
    filters.id = value ? parseInt(value) : undefined
  },
  { debounce: 500 }
)

watchDebounced(
  policyGroupInput,
  (value) => {
    filters.policy_group_number = value || undefined
  },
  { debounce: 500 }
)

const handleDelete = (invoiceID: number, e: MouseEvent) => {
  e.stopPropagation()
  DeleteConfirmationModal(
    'Delete Invoice',
    'Are you sure you want to delete this invoice?',
    invoiceID,
    deletePremiumInvoiceMutation.mutateAsync
  )
}

const handleExport = async (invoice: IPremiumInvoice) => {
  try {
    if (invoice.file_url) {
      downloadLinkTarget(invoice.file_url, `invoice_${invoice.id}.pdf`)
    }
  } catch (err: any) {
    NotifyFail(`Failed to download invoice: ${err.message}`)
  }
}

// Status editing handlers
const startEditingStatus = (invoiceId: number, currentStatus?: InvoiceStatus) => {
  editingStatus.value[invoiceId] = true
  selectedStatus.value[invoiceId] = currentStatus
}

const cancelEditingStatus = (invoiceId: number) => {
  editingStatus.value[invoiceId] = false
  selectedStatus.value[invoiceId] = undefined
}

const onStatusChange = (invoiceId: number, statusValue?: InvoiceStatus) => {
  const form = { status: statusValue }
  updatePremiumInvoiceMutation.mutate(
    { id: invoiceId, data: form },
    {
      onSettled: () => {
        editingStatus.value[invoiceId] = false
      },
    }
  )
}

const onCustomerChange = (customer: any) => {
  filters.customer_id = customer?.id
}

const handleDateRangeChange = (newRange: Array<string> | null) => {
  if (!newRange) {
    filters.start_date = undefined
    filters.end_date = undefined
    return
  }

  filters.start_date = newRange[0]
  filters.end_date = newRange[1]
}

const filterSettings = [
  {
    key: 'status',
    value: 'Status',
    options: InvoiceStatusOptions.map((v) => ({ key: v, value: v })),
    type: 'dropdown',
    placeholder: 'Status',
    modelValue: statusFilter,
    onChange: (val: string) => {
      filters.status = val
    },
    iconClass: 'label-outline-rounded',
    style: 'width: 200px',
  },
  {
    key: 'type',
    value: 'Type',
    options: Object.values(InvoiceType).map((v) => ({ key: v, value: v })),
    type: 'dropdown',
    placeholder: 'Type',
    modelValue: typeFilter,
    onChange: (val: string) => {
      filters.type = val
    },
    iconClass: 'package-2-outline',
    style: 'width: 200px',
  },
]
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-else>
    <div class="flex justify-between mb-4">
      <div class="flex gap-2 items-start flex-wrap max-h-20">
        <el-input
          v-model="invoiceIDInput"
          v-maska
          data-maska="9"
          data-maska-tokens="9:[0-9]:repeated"
          placeholder="Invoice #"
          clearable
          style="width: 100px"
          :disabled="isFetching"
        />
        <el-date-picker
          v-model="dateRangeFilter"
          :disabled="isFetching"
          type="daterange"
          range-separator="to"
          start-placeholder="Start Issue Date"
          end-placeholder="End Issue Date"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD"
          style="width: 300px"
          @change="handleDateRangeChange"
        />
        <RcCustomerSelect :disabled="isFetching" class="w-56" @change="onCustomerChange" />
        <el-input
          v-model="policyGroupInput"
          placeholder="Policy Group Number"
          clearable
          style="width: 200px"
          :disabled="isFetching"
        />
        <TableFilters :filters-list="filterSettings" />
      </div>
    </div>
    <el-table v-loading="isFetching" :data="data?.data" empty-text="No data" stripe>
      <el-table-column type="expand" width="50">
        <template #default="scope">
          <div class="p-4">
            <!-- Premium Details Section -->
            <div class="mb-4">
              <h4 class="text-sm font-medium mb-3" style="color: var(--text-secondary)">Premium Details</h4>
              <div class="grid grid-cols-4 gap-3">
                <div class="p-2 rounded" style="background-color: var(--fill-secondary)">
                  <div class="text-xs mb-1" style="color: var(--text-tertiary)">Policies Quantity</div>
                  <div class="text-sm font-medium">
                    {{ scope.row.invoiced_policies_quantity }}
                  </div>
                </div>
                <div class="p-2 rounded" style="background-color: var(--fill-secondary)">
                  <div class="text-xs mb-1" style="color: var(--text-tertiary)">Policy Group Premium</div>
                  <div class="text-sm font-medium">
                    {{ formatCurrency(parseFloat(scope.row.policy_group_premium || 0)) }}
                  </div>
                </div>
                <div class="p-2 rounded" style="background-color: var(--fill-secondary)">
                  <div class="text-xs mb-1" style="color: var(--text-tertiary)">Underwriting Fee</div>
                  <div class="text-sm font-medium">
                    {{ formatCurrency(parseFloat(scope.row.policy_underwriting_fee || 0)) }}
                  </div>
                </div>
                <div
                  v-if="scope.row.commission_percentage"
                  class="p-2 rounded"
                  style="background-color: var(--fill-secondary)"
                >
                  <div class="text-xs mb-1" style="color: var(--text-tertiary)">Commission Percentage</div>
                  <div class="text-sm font-medium">{{ scope.row.commission_percentage || 0 }}%</div>
                </div>
              </div>
            </div>

            <!-- Fees Section -->
            <div v-if="scope.row.fees && scope.row.fees.length > 0">
              <h4 class="text-sm font-medium mb-3" style="color: var(--text-secondary)">Fees</h4>
              <el-table :data="scope.row.fees" size="small">
                <el-table-column label="Fees" header-align="center">
                  <el-table-column prop="type" label="Type" />
                  <el-table-column prop="amount" label="Amount" :formatter="currencyFormatter" />
                  <el-table-column prop="payable_from" label="Payable From" :min-width="120" />
                  <el-table-column prop="payable_to" label="Payable To" :min-width="120" />
                </el-table-column>
              </el-table>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="id" label="Invoice #" :min-width="80" />
      <el-table-column prop="customer" label="Customer" :min-width="150" />
      <el-table-column prop="policy_group_number" label="Policy Group #" :min-width="120" />
      <el-table-column prop="issue_date" label="Issue Date" :min-width="100" :formatter="dateFormatter" />
      <el-table-column prop="due_date" label="Due Date" :min-width="100" :formatter="dateFormatter" />
      <el-table-column prop="type" label="Type" :min-width="100" />
      <el-table-column prop="amount" label="Amount" :min-width="100">
        <template #default="scope">
          <span :style="{ color: getCostColor(scope.row.amount) }">
            {{ formatCurrency(parseFloat(scope.row.amount)) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="Status" :min-width="160">
        <template #default="scope">
          <!-- Displaying -->
          <div v-if="!editingStatus[scope.row.id]" class="flex gap-2">
            <el-tag
              v-if="scope.row.status"
              disable-transitions
              :color="getStatusTagColor(scope.row.status)"
              class="inline-flex gap-1 cursor-pointer"
              style="border: none"
              @click="startEditingStatus(scope.row.id, scope.row.status)"
            >
              <span :style="{ color: getStatusColor(scope.row.status) }">
                {{ scope.row.status }}
              </span>
              <Icon
                :color="getStatusColor(scope.row.status)"
                icon="material-symbols:keyboard-arrow-down-rounded"
                :height="16"
              />
            </el-tag>
            <el-tag
              v-else
              disable-transitions
              color="var(--el-fill-color-lighter)"
              class="inline-flex gap-1 cursor-pointer"
              style="border: 1px dashed var(--el-border-color); color: var(--el-text-color-placeholder)"
              @click="startEditingStatus(scope.row.id, scope.row.status)"
            >
              <span>Status</span>
              <Icon
                color="var(--el-text-color-placeholder)"
                icon="material-symbols:keyboard-arrow-down-rounded"
                :height="16"
              />
            </el-tag>
          </div>
          <!-- Editing -->
          <span v-else class="text-nowrap flex gap-2">
            <el-select
              :model-value="selectedStatus[scope.row.id]"
              :min-width="160"
              :disabled="updatePremiumInvoiceMutation.isPending.value"
              :loading="updatePremiumInvoiceMutation.isPending.value"
              clearable
              @click.stop
              @change="(val) => onStatusChange(scope.row.id, val)"
            >
              <el-option v-for="opt in InvoiceStatusOptions" :key="opt" :label="opt" :value="opt" />
            </el-select>

            <el-button
              :disabled="updatePremiumInvoiceMutation.isPending.value"
              link
              @click.stop="cancelEditingStatus(scope.row.id)"
            >
              <Icon icon="material-symbols:close" :height="16" />
            </el-button>
          </span>
        </template>
      </el-table-column>
      <el-table-column :width="60" align="right" fixed="right">
        <template #default="scope">
          <div class="flex justify-end">
            <el-button
              v-if="scope.row.file_url"
              link
              title="View Invoice"
              @click="handleExport(scope.row as IPremiumInvoice)"
            >
              <Icon icon="material-symbols:download" :height="16" />
            </el-button>
            <el-button
              :disabled="scope.row.status === InvoiceStatus.PAID"
              link
              title="Delete"
              @click="handleDelete(scope.row.id, $event)"
            >
              <Icon icon="material-symbols:delete-outline" :height="16" />
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      v-if="data && data.total_pages > 0"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      class="self-end mt-4"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="data.total_pages"
      :total="data.total_records"
      :disabled="isFetching"
      :layout="DEFAULT_PAGINATION_LAYOUT"
      background
    />
  </template>
</template>

<style scoped lang="scss">
:deep(.el-tag__content) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
</style>
